import {useQuery} from '@apollo/client';
import {Form, Input, Select, Switch, Tooltip} from 'antd';
import v2 from '../../v2/instrumentV2.module.scss';
import {FormCard, spans} from '../../v2/FormCard';
import {InstrumentActions} from '../../v2/InstrumentActions';
import {useRemainingFields} from '../../v2/useRemainingFields';
import {generalDetailsMock, SAMPLE_IMAGE_URL} from '../../v2/devPrefill';
import {isDev, NavTabsEnum, supportedLanguages} from 'utils/constants';
import {currencyCodes} from 'pages/adminOpportunities/stocksListings/create/CreateStock';
import {INSTRUMENTS_DETAILS_QUERIES} from 'graphql/queries/assetManagement';
import {useEffect, useState} from 'react';
import {issuerTypes} from 'pages/AssetManagement/modules/constants';
import {GET_ALL_STOCKS_WITH_INSTRUMENT_KEY} from 'graphql/queries/stocks';
import {toast} from 'react-toastify';
import {useParams} from 'react-router-dom';
import UploadImage from 'components/Startup/profile/companyImage';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';

const resolveLanguage = (fromRecord, fromRoute) => {
  const isSupported = value => supportedLanguages.some(l => l.value === value);
  if (isSupported(fromRecord)) return fromRecord;
  if (isSupported(fromRoute)) return fromRoute;
  return 'EN';
};

export const BaseGeneralDetails = ({onSubmit, title, existingValues}) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [icon, setIcon] = useState(null);
  const [form] = Form.useForm();
  const {id, assetId, lang} = useParams();
  const {data: getAllQueries, loading} = useQuery(INSTRUMENTS_DETAILS_QUERIES);
  const {data: stockExchange, loading: stockLoading} = useQuery(
    GET_ALL_STOCKS_WITH_INSTRUMENT_KEY,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error),
    },
  );

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  const [remaining, onValuesChange] = useRemainingFields(
    form,
    [
      'name',
      'description',
      'issuer',
      'issuerType',
      'listingType',
      'currency',
      'exchange',
      'language',
      ...(assetId === 'FUNDS' ? [] : ['sectorIndustry']),
    ],
    existingValues,
  );

  useEffect(() => {
    if (id) {
      setIcon(existingValues?.icon);
      setPreviewUrl(existingValues?.icon);
      form.setFieldsValue({
        ...existingValues,
        sectorIndustry: existingValues?.sectorIndustry?.id,
        topics:
          existingValues?.topics
            ?.filter(topic => topic?.id != null)
            .map(topic => topic?.id) || [],
        issuer: existingValues?.issuer?.id,
        issuerType: existingValues?.issuer?.issuerType,
        // `exchange` resolves as an object now; the Select is keyed on its id.
        exchange: existingValues?.exchange?.id ?? existingValues?.exchange,
        currency: existingValues?.currency?.currencyCode,
        // Funds cannot return `language` — it is non-nullable in the schema but
        // null in the data, so selecting it fails the whole read. Fall back to
        // the language in the route, and to EN when that is absent too: fund
        // records with a null languageVersions entry produce a literal "null"
        // in the URL. EN matches the gateway's own resolution order.
        language: resolveLanguage(existingValues?.language, lang),
      });
    } else {
      setIcon(existingValues?.icon);
      setPreviewUrl(existingValues?.icon);
      form.setFieldsValue({...existingValues});
    }
  }, [existingValues]);

  return (
    <div>
      <div className={v2.bodyHead}>
        <h2>General details</h2>
        <p>
          Who issues this {title.toLowerCase()}, how it is classified, and which
          exchange it lists on.
        </p>
      </div>

      <Form
        onFinish={values => {
          if (!icon && assetId === 'FUNDS')
            return toast.error('Icon is Required');
          onSubmit({...values, icon});
        }}
        form={form}
        onValuesChange={onValuesChange}
        layout="vertical">
        <FormCard
          title="Identity"
          description={`How this ${title.toLowerCase()} is presented across the platform`}>
          <div className={spans.full}>
            <UploadImage
              label="Upload Icon"
              isACL
              previewUrl={previewUrl}
              setPreviewUrl={setPreviewUrl}
              setImgUrl={setIcon}
              folder={'bonds'}
              bucket={
                isDev
                  ? 'dabafinancialinstruments-nonprod'
                  : 'dabafinancialinstruments-prod'
              }
            />
          </div>
          <div className={spans.full}>
            <Form.Item
              rules={[{required: true, message: 'This is a Required Field'}]}
              name="name"
              label={`${title} Name`}>
              <Input />
            </Form.Item>
          </div>
          <div className={spans.full}>
            <Form.Item
              rules={[{required: true, message: 'This is a Required Field'}]}
              name="description"
              label="Description">
              <Input.TextArea rows={5} />
            </Form.Item>
          </div>
        </FormCard>

        <FormCard
          title="Issuer and classification"
          description="Drives how this instrument is grouped and discovered">
          <div className={spans.half}>
            <Form.Item
              rules={[{required: true, message: 'This is a Required Field'}]}
              name="issuer"
              label="Issuer">
              <Select
                showSearch
                allowClear
                disabled={loading}
                filterOption={true}
                optionFilterProp="filter"
                loading={loading}>
                {loading ? (
                  <Select.Option disabled key="Loading...">
                    Loading Issuers...
                  </Select.Option>
                ) : (
                  getAllQueries?.getAllCompanies?.map(company => (
                    <Select.Option
                      filter={`${company?.name}${company?.ticker}`}
                      key={company?.id}>
                      <div className="all-data">
                        <img src={company?.logoImgURL} alt="" />
                        <span>{company?.name}</span>
                      </div>
                    </Select.Option>
                  ))
                )}
              </Select>
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item
              rules={[{required: true, message: 'This is a Required Field'}]}
              name="issuerType"
              label="Issuer Type">
              <Select>
                {issuerTypes.map(data => (
                  <Select.Option key={data.value}>{data.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item name="topics" label="Topics">
              <Select
                showSearch
                allowClear
                mode="multiple"
                filterOption={true}
                disabled={loading}
                optionFilterProp="filter"
                loading={loading}>
                {loading ? (
                  <Select.Option disabled key="Loading...">
                    Loading Topics...
                  </Select.Option>
                ) : (
                  getAllQueries?.getAllTopics?.map(company => (
                    <Select.Option
                      filter={`${company?.name}`}
                      key={company?.id}>
                      <div key={company.id} className="all-data">
                        <img src={company?.icon} alt="" />
                        <span>{company?.name}</span>
                      </div>
                    </Select.Option>
                  ))
                )}
              </Select>
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item
              name="sectorIndustry"
              rules={[
                {
                  required: assetId !== 'FUNDS',
                  message: 'Please select an industry',
                },
              ]}
              label="Industry">
              <Select
                showSearch
                allowClear
                filterOption={true}
                disabled={loading}
                optionFilterProp="filter"
                loading={loading}>
                {loading ? (
                  <Select.Option disabled key="Loading...">
                    Loading Industries...
                  </Select.Option>
                ) : (
                  getAllQueries?.getAllIndustries?.map(company => (
                    <Select.Option
                      filter={`${company?.name}`}
                      key={company?.id}>
                      <div className="all-data">
                        <img src={company?.icon} alt="" />
                        <span>{company?.name}</span>
                      </div>
                    </Select.Option>
                  ))
                )}
              </Select>
            </Form.Item>
          </div>
        </FormCard>

        <FormCard
          title="Listing"
          description="The exchange and currency this instrument trades in">
          <div className={spans.half}>
            <Form.Item
              rules={[{required: true, message: 'This is a Required Field'}]}
              name="listingType"
              label="Listing Type">
              <Select placeholder="Listed">
                <Select.Option value="LISTED">Listed</Select.Option>
                <Select.Option value="UNLISTED">Unlisted</Select.Option>
              </Select>
            </Form.Item>
          </div>
          <div className={spans.half}>
            <Form.Item
              rules={[{required: true, message: 'This is a Required Field'}]}
              name="currency"
              label="Select Currency">
              <Select>
                {currencyCodes.map(data => (
                  <Select.Option key={data.value}>{data.value}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div className={spans.twoThirds}>
            <Form.Item
              name="exchange"
              rules={[{required: true, message: 'This is a Required Field'}]}
              label="Select Stock Exchange">
              <Select
                loading={stockLoading}
                placeholder="Select stock exchange"
                showSearch
                disabled={stockLoading}
                optionFilterProp="children">
                {stockLoading ? (
                  <Select.Option disabled key="Loading">
                    Loading...
                  </Select.Option>
                ) : (
                  stockExchange?.getAllStocksExchange?.map(item => (
                    <Select.Option value={item?.id} key={item?.id}>
                      {item?.code ? `${item.code} — ` : ''}
                      {item.stockExchangeName}
                    </Select.Option>
                  ))
                )}
              </Select>
            </Form.Item>
          </div>
          <div className={spans.third}>
            <Form.Item
              rules={[{required: true, message: 'This is a Required Field'}]}
              name="language"
              label="Select Language">
              <Select disabled={id}>
                {supportedLanguages.map(data => (
                  <Select.Option key={data.value}>{data.label}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </FormCard>

        <FormCard
          title="Compliance"
          description="Flags that affect who can see and trade this instrument">
          <div className={spans.half}>
            <Form.Item
              name="shariaCompliant"
              valuePropName="checked"
              label={
                <Tooltip title="Does this investment comply with Sharia law">
                  Sharia Compliant
                </Tooltip>
              }>
              <Switch />
            </Form.Item>
          </div>
        </FormCard>

        <InstrumentActions
          onPrepopulate={() => {
            // Issuer, exchange, sector and topics must be real ids, so take
            // the first of whatever this screen has actually loaded.
            form.setFieldsValue(
              generalDetailsMock({
                issuer: getAllQueries?.getAllCompanies?.[0]?.id,
                exchange: stockExchange?.getAllStocksExchange?.[0]?.id,
                sectorIndustry: getAllQueries?.getAllIndustries?.[0]?.id,
                topics: getAllQueries?.getAllTopics
                  ?.slice(0, 2)
                  .map(topic => topic?.id)
                  .filter(Boolean),
              }),
            );
            setIcon(SAMPLE_IMAGE_URL);
            setPreviewUrl(SAMPLE_IMAGE_URL);
            onValuesChange();
          }}
          remaining={remaining}
          disabled={!pageOperations?.update}
          label={!id ? 'Save and proceed' : 'Update'}
        />
      </Form>
    </div>
  );
};
